import { useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Search, Filter, Eye, Edit, Trash2, Plus, Globe, ToggleLeft, ToggleRight, Calendar, Hash } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { staticPagesService, type StaticPage, type StaticPageFilters, type CreateStaticPageRequest } from "@/services/staticPagesService";

export default function StaticContentPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<StaticPageFilters>({
    page: 1,
    limit: 10
  });
  const [selectedPage, setSelectedPage] = useState<StaticPage | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<StaticPage | null>(null);
  const [formData, setFormData] = useState<CreateStaticPageRequest>({
    title_ar: '',
    title_en: '',
    content_ar: '',
    content_en: '',
    slug: ''
  });

  // Fetch static pages data
  const { data: pagesResponse, isLoading: pagesLoading, error: pagesError } = useQuery({
    queryKey: ['static-pages', filters],
    queryFn: () => staticPagesService.getStaticPages(filters),
    staleTime: 30000,
  });

  const allPages = pagesResponse?.data?.pages || [];
  
  // Apply client-side filtering as fallback if API doesn't handle filters
  const pages = React.useMemo(() => {
    let filtered = [...allPages];
    
    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter((page: any) => 
        page.title_ar?.toLowerCase().includes(searchTerm) ||
        page.title_en?.toLowerCase().includes(searchTerm) ||
        page.slug?.toLowerCase().includes(searchTerm) ||
        page.content_ar?.toLowerCase().includes(searchTerm) ||
        page.content_en?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply active filter
    if (filters.is_active !== undefined) {
      filtered = filtered.filter((page: any) => page.is_active === filters.is_active);
    }
    
    console.log('🔧 Static pages filtering applied:', {
      original: allPages.length,
      filtered: filtered.length,
      filters
    });
    
    return filtered;
  }, [allPages, filters]);
  
  // Add logging when data changes
  React.useEffect(() => {
    console.log('🔧 Static pages query result:', pagesResponse);
    console.log('🔧 Static pages error:', pagesError);
    console.log('🔧 All pages:', allPages);
    console.log('🔧 Filtered pages:', pages);
    console.log('🔧 Current filters:', filters);
  }, [pagesResponse, pagesError, allPages, pages, filters]);
  
  // Calculate stats from pages data
  const calculatedStats = {
    total_pages: pages.length,
    active_pages: pages.filter((p: any) => p.is_active).length,
    inactive_pages: pages.filter((p: any) => !p.is_active).length,
  };
  
  const stats = calculatedStats;

  // Create page mutation
  const createPageMutation = useMutation({
    mutationFn: (data: CreateStaticPageRequest) => staticPagesService.createStaticPage(data),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("pageCreatedSuccessfully") || "Page created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['static-pages'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to create page",
        variant: "destructive",
      });
    },
  });

  // Update page mutation
  const updatePageMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => staticPagesService.updateStaticPage(id, data),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("pageUpdatedSuccessfully") || "Page updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['static-pages'] });
      setIsCreateModalOpen(false);
      setEditingPage(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to update page",
        variant: "destructive",
      });
    },
  });

  // Delete page mutation
  const deletePageMutation = useMutation({
    mutationFn: (id: number) => staticPagesService.deleteStaticPage(id),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("pageDeletedSuccessfully") || "Page deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['static-pages'] });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to delete page",
        variant: "destructive",
      });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => 
      staticPagesService.toggleActive(id, is_active),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("pageStatusUpdated") || "Page status updated",
      });
      queryClient.invalidateQueries({ queryKey: ['static-pages'] });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to update page status",
        variant: "destructive",
      });
    },
  });

  const handleFilterChange = (key: keyof StaticPageFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      content_ar: '',
      content_en: '',
      slug: ''
    });
  };

  const handleCreatePage = () => {
    resetForm();
    setEditingPage(null);
    setIsCreateModalOpen(true);
  };

  const handleEditPage = (page: StaticPage) => {
    setEditingPage(page);
    setFormData({
      title_ar: page.title_ar,
      title_en: page.title_en,
      content_ar: page.content_ar,
      content_en: page.content_en,
      slug: page.slug
    });
    setIsCreateModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title_ar.trim()) {
      toast({
        title: t("error"),
        description: t("titleArRequired") || "Arabic title is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.title_en.trim()) {
      toast({
        title: t("error"),
        description: t("titleEnRequired") || "English title is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.slug.trim()) {
      toast({
        title: t("error"),
        description: t("slugRequired") || "Slug is required",
        variant: "destructive",
      });
      return;
    }

    // Check for duplicate slugs (only when creating or changing slug)
    const isDuplicateSlug = pages.some((page: any) => 
      page.slug.toLowerCase() === formData.slug.trim().toLowerCase() && 
      (!editingPage || page.id !== editingPage.id)
    );
    
    if (isDuplicateSlug) {
      toast({
        title: t("error"),
        description: t("duplicateSlug") || "A page with this slug already exists",
        variant: "destructive",
      });
      return;
    }

    if (editingPage) {
      updatePageMutation.mutate({ id: editingPage.id, data: formData });
    } else {
      createPageMutation.mutate(formData);
    }
  };

  const handleDeletePage = (id: number) => {
    deletePageMutation.mutate(id);
  };

  const handleToggleActive = (id: number, currentActive: boolean) => {
    toggleActiveMutation.mutate({ id, is_active: !currentActive });
  };

  const handleViewPage = (page: StaticPage) => {
    setSelectedPage(page);
    setIsViewModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const generateSlugFromTitle = (title: string) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? 'default' : 'secondary'}>
        {isActive ? (t("active") || "Active") : (t("inactive") || "Inactive")}
      </Badge>
    );
  };

  if (pagesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">{t("loading")}...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("staticContent") || "Static Content"}</h1>
          <p className="text-muted-foreground">
            {t("staticContentSubtitle") || "Manage static pages and content"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreatePage}>
            <Plus className="mr-2 h-4 w-4" />
            {t("addPage") || "Add Page"}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              console.log('🔧 Testing direct API call...');
              try {
                const response = await fetch('http://localhost:3000/admin/pages', {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                });
                const data = await response.json();
                console.log('🔧 Direct API response:', data);
                console.log('🔧 Response status:', response.status);
              } catch (error) {
                console.error('🔧 Direct API error:', error);
              }
            }}
          >
            Test API
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalPages") || "Total Pages"}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_pages}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("activePages") || "Active Pages"}</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_pages}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("inactivePages") || "Inactive Pages"}</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.inactive_pages}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("filters") || "Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder={t("searchPages") || "Search by title, slug, or content..."}
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select
              value={filters.is_active !== undefined ? filters.is_active.toString() : 'all'}
              onValueChange={(value) => handleFilterChange('is_active', value === 'all' ? undefined : value === 'true')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("status") || "Status"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  search: '',
                  is_active: undefined,
                  page: 1,
                  limit: 10
                });
              }}
              className="whitespace-nowrap"
            >
              {t("clearFilters") || "Clear Filters"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pages Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("pagesList") || "Pages List"}</CardTitle>
              <CardDescription>
                {t("managePages") || "Manage your static pages and content"}
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              {pages.length > 0 ? (
                <>
                  {t("showing") || "Showing"} {pages.length} {t("of") || "of"} {allPages.length} {t("pages") || "pages"}
                  {(filters.search || filters.is_active !== undefined) && (
                    <span className="ml-2 text-primary">({t("filtered") || "filtered"})</span>
                  )}
                </>
              ) : (
                <span>{t("noPages") || "No pages found"}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {pages.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                {t("noPages") || "No pages found"}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("noPagesMatch") || "No pages match your current filters"}
              </p>
              <Button onClick={handleCreatePage} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                {t("createFirstPage") || "Create your first page"}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("title") || "Title"}</TableHead>
                  <TableHead>{t("slug") || "Slug"}</TableHead>
                  <TableHead>{t("status") || "Status"}</TableHead>
                  <TableHead>{t("lastUpdated") || "Last Updated"}</TableHead>
                  <TableHead>{t("actions") || "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pages.map((page: any) => (
                  <TableRow key={page.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{page.title_en || page.title_ar}</div>
                        <div className="text-sm text-muted-foreground">{page.title_ar}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Hash className="h-3 w-3 text-muted-foreground" />
                        <code className="text-sm bg-muted px-1 rounded">{page.slug}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(page.is_active)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(page.updated_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewPage(page)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPage(page)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(page.id, page.is_active)}
                          disabled={toggleActiveMutation.isPending}
                        >
                          {page.is_active ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={deletePageMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("deletePage") || "Delete Page"}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("deletePageConfirmation") || "Are you sure you want to delete this page? This action cannot be undone."}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeletePage(page.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("delete") || "Delete"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Page Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? (t("editPage") || "Edit Page") : (t("addPage") || "Add Page")}
            </DialogTitle>
            <DialogDescription>
              {editingPage 
                ? (t("editPageDescription") || "Update page content and settings")
                : (t("addPageDescription") || "Create a new static page")
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title_en">{t("titleEnglish") || "Title (English)"}</Label>
                <Input
                  id="title_en"
                  value={formData.title_en}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      title_en: value,
                      // Auto-generate slug from English title if not editing
                      slug: !editingPage && !formData.slug ? generateSlugFromTitle(value) : prev.slug
                    }));
                  }}
                  placeholder="Privacy Policy"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_ar">{t("titleArabic") || "Title (Arabic)"}</Label>
                <Input
                  id="title_ar"
                  value={formData.title_ar}
                  onChange={(e) => setFormData(prev => ({ ...prev, title_ar: e.target.value }))}
                  placeholder="سياسة الخصوصية"
                  dir="rtl"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">{t("slug") || "Slug"}</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="privacy-policy"
                required
              />
              <p className="text-sm text-muted-foreground">
                {t("slugHelp") || "URL-friendly identifier (e.g., privacy-policy)"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_en">{t("contentEnglish") || "Content (English)"}</Label>
              <Textarea
                id="content_en"
                value={formData.content_en}
                onChange={(e) => setFormData(prev => ({ ...prev, content_en: e.target.value }))}
                placeholder="<h2>Privacy Policy</h2><p>Your privacy policy content here...</p>"
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-sm text-muted-foreground">
                {t("htmlSupported") || "HTML tags are supported"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content_ar">{t("contentArabic") || "Content (Arabic)"}</Label>
              <Textarea
                id="content_ar"
                value={formData.content_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, content_ar: e.target.value }))}
                placeholder="<h2>سياسة الخصوصية</h2><p>محتوى سياسة الخصوصية هنا...</p>"
                rows={8}
                dir="rtl"
                className="font-mono text-sm"
              />
              <p className="text-sm text-muted-foreground">
                {t("htmlSupported") || "HTML tags are supported"}
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateModalOpen(false)}
              >
                {t("cancel") || "Cancel"}
              </Button>
              <Button
                type="submit"
                disabled={createPageMutation.isPending || updatePageMutation.isPending}
              >
                {(createPageMutation.isPending || updatePageMutation.isPending) ? 
                  (t("saving") || "Saving...") : 
                  (editingPage ? (t("updatePage") || "Update Page") : (t("createPage") || "Create Page"))
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Page Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("pageDetails") || "Page Details"}</DialogTitle>
            <DialogDescription>
              {t("viewPageDetails") || "View detailed information about this page"}
            </DialogDescription>
          </DialogHeader>
          {selectedPage && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">{t("titleEnglish") || "Title (English)"}</h4>
                  <p className="text-sm bg-muted p-3 rounded-md">{selectedPage.title_en}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("titleArabic") || "Title (Arabic)"}</h4>
                  <p className="text-sm bg-muted p-3 rounded-md" dir="rtl">{selectedPage.title_ar}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">{t("slug") || "Slug"}</h4>
                  <code className="text-sm bg-muted p-2 rounded-md block">{selectedPage.slug}</code>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("status") || "Status"}</h4>
                  {getStatusBadge(selectedPage.is_active)}
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("lastUpdated") || "Last Updated"}</h4>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(selectedPage.updated_at)}
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t("contentEnglish") || "Content (English)"}</h4>
                <div className="text-sm bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                  <div dangerouslySetInnerHTML={{ __html: selectedPage.content_en }} />
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t("contentArabic") || "Content (Arabic)"}</h4>
                <div className="text-sm bg-muted p-3 rounded-md max-h-40 overflow-y-auto" dir="rtl">
                  <div dangerouslySetInnerHTML={{ __html: selectedPage.content_ar }} />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}